import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import WebPageLayout from '../../../components/layout/WebPageLayout';
import DSLoading from '../../../components/designSystem/DSLoading';
import { convertToVwWeb, cappedVwWeb } from '../../../Utils/responsive';
import hugeIcon from '../../../assets/Icon/project/huge_icon.png';
import linkIcon from '../../../assets/Icon/project/link_icon.png';
import weuiArrowIcon from '../../../assets/Icon/project/weui_arrow-outlined.png';
import postsIcon from '../../../assets/Icon/project/posts_icon.png';
import donorsIcon from '../../../assets/Icon/project/donors_icon.png';
import paymentsIcon from '../../../assets/Icon/project/payments_icon.png';
import settingsIcon from '../../../assets/Icon/project/settings_icon.png';
import logoImg from '../../../assets/Logo/main_logo.png';
import { PATHS } from '../../../routes/paths';
import DSModal from '../../../components/designSystem/DSModal';
import ProfileIncompleteModal from '../../../components/common/ProfileIncompleteModal';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { createWithdraw, getWithdraw, searchWithdrawProjects, updateProject, updateWithdraw } from '../../../Module/Project/Presentation/projectActions';
import { selectProjects, selectProjectsStatus } from '../../../Module/Project/Presentation/projectSelectors';
import { selectSession } from '../../../Module/Auth/Presentation/authSelectors';
import { selectUserProfile } from '../../../Module/User/Presentation/userSelectors';
import { updateUser } from '../../../Module/User/Presentation/userActions';
import { useTranslation } from 'react-i18next';
import { getYouTubeThumbnail } from '../../../Utils/youtube';
import { getCloudFrontImageUrl, THUMBNAIL_DIMENSIONS } from '../../../Utils/cloudFrontImage';
import { canCreateNewProject } from '../../../Utils/projectCreationGuard';

type FundingType = 'recurring' | 'oneTime';
type ProjectStatus = 'inProgress' | 'done';
type PayAction = 'request' | 'completed';

type ProjectCardItem = {
  id: string;
  withdrawProjectId: string;
  projectStatus: string;
  title: string;
  dateText: string;
  fundingTypeLabel: FundingType;
  statusLabel: ProjectStatus;
  imageUrl?: string;
  payAction?: PayAction;
};

type PayFlowState =
  | { step: 'idle' }
  | { step: 'processing' }
  | { step: 'inProgress' }
  | {
      step: 'codeReady';
      code: string;
      input: string;
      failCount: number;
      error?: string;
      withdrawId?: string;
      requestCode?: string;
      withdrawSnapshot?: Record<string, unknown>;
    }
  | { step: 'done' }
  | { step: 'stopped'; failCount: number };

const toBooleanFlag = (value: unknown) => {
  if (typeof value === 'boolean') return value;
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'y';
};

const toTrialCount = (value: unknown, fallback = 0) => {
  const parsed = Number(value ?? fallback);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return parsed;
};

const resolveWithdrawPayFlow = ({
  snapshot,
  withdrawId,
  code,
  requestCode,
  fallbackFailCount = 0,
}: {
  snapshot?: Record<string, unknown>;
  withdrawId?: string;
  code?: string;
  requestCode?: string;
  fallbackFailCount?: number;
}): PayFlowState => {
  const data = snapshot ?? {};
  const failCount = toTrialCount((data as any)?.user_trials ?? (data as any)?.trials, fallbackFailCount);
  const resolvedWithdrawId = String(withdrawId ?? (data as any)?.withdraw_id ?? '').trim();
  const isSentAuthenticationCode = toBooleanFlag((data as any)?.is_sent_authentication_code);
  const isResponseAuthenticationCode = toBooleanFlag((data as any)?.is_response_authentication_code);
  const isSentFund = toBooleanFlag((data as any)?.is_sent_fund);

  if (isSentFund) {
    return { step: 'done' };
  }

  if (isSentAuthenticationCode && isResponseAuthenticationCode) {
    return { step: 'inProgress' };
  }

  if (isSentAuthenticationCode && !isResponseAuthenticationCode) {
    if (failCount >= 3) {
      return { step: 'stopped', failCount };
    }
    return {
      step: 'codeReady',
      code: code ?? '',
      requestCode: requestCode ?? '',
      withdrawId,
      withdrawSnapshot: data,
      input: '',
      failCount,
    };
  }

  // withdraw가 생성된 상태(요청 완료)인데 아직 인증코드가 발송되지 않은 구간은 처리중으로 본다.
  if (resolvedWithdrawId) {
    return { step: 'processing' };
  }

  // 지급요청이 생성되지 않았거나(code 발송 전) 기본 상태는 idle로 유지한다.
  return { step: 'idle' };
};

const sanitizeProjectTitle = (value: string) => value.replace(/<br\s*\/?>/gi, ' ').replace(/\r?\n/g, ' ').trim();

const ProjectManagementWeb = () => {
  const { t } = useTranslation('translation');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const projects = useAppSelector(selectProjects);
  const status = useAppSelector(selectProjectsStatus);
  const session = useAppSelector(selectSession);
  const userProfile = useAppSelector(selectUserProfile);
  const [openManageId, setOpenManageId] = useState<string | null>(null);
  const openManageWrapRef = useRef<HTMLDivElement | null>(null);
  const [confirmPayProject, setConfirmPayProject] = useState<{
    cardId: string;
    withdrawProjectId: string;
  } | null>(null);
  const [successPayProjectId, setSuccessPayProjectId] = useState<string | null>(null);
  const [failedPayProjectId, setFailedPayProjectId] = useState<string | null>(null);
  const [isProfileIncompleteModalOpen, setIsProfileIncompleteModalOpen] = useState(false);
  const [focusedVerifyInputId, setFocusedVerifyInputId] = useState<string | null>(null);
  const [payFlowById, setPayFlowById] = useState<Record<string, PayFlowState>>({});
  const timersRef = useRef<Record<string, number>>({});

  const userId = useMemo(() => {
    return (
      userProfile?.user_id ||
      (session as any)?.user?._id ||
      (session as any)?.user?.id ||
      window.localStorage.getItem('missionfund.userId') ||
      ''
    );
  }, [session, userProfile?.user_id]);
  const isLoading =
    Boolean(userId) &&
    projects.length === 0 &&
    (status === 'idle' || status === 'loading');

  const userDeviceId = useMemo(() => {
    return (
      userProfile?.device_id ||
      (session as any)?.user?.device_id ||
      window.localStorage.getItem('missionfund.deviceId') ||
      ''
    );
  }, [session, userProfile?.device_id]);

  const payConfirmDescription = t('page.projectManagement.pay.confirmDescription');
  const paySuccessDescription = t('page.projectManagement.pay.successDescription');
  const payFailTitle = t('page.projectManagement.pay.failedTitle');
  const payFailDescription = t('page.projectManagement.pay.failedDescription');

  const toFundingTypeLabel = useCallback((raw?: string): FundingType => {
    const normalized = String(raw ?? '').toUpperCase();
    if (['OT', 'ONCE', 'ONE_TIME', 'ONETIME'].includes(normalized)) return 'oneTime';
    return 'recurring';
  }, []);

  useEffect(() => {
    if (!userId || status === 'loading') return;
    
    dispatch(
      searchWithdrawProjects({
        limit: 100,
        params: [{ fld: 'user_id', val: userId, op: 'eq' }],
        sort: 'created_at:desc',
      }),
    );
  }, [dispatch, userId]);

  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach((t) => window.clearTimeout(t));
      timersRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (!openManageId) return;

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (openManageWrapRef.current && openManageWrapRef.current.contains(target)) return;
      setOpenManageId(null);
    };

    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [openManageId]);

  const handleCreateProject = useCallback(() => {
    if (!canCreateNewProject(userProfile)) {
      setIsProfileIncompleteModalOpen(true);
      return;
    }
    navigate(PATHS.NEW_PROJECT);
  }, [navigate, userProfile]);

  const handleManageProject = useCallback((projectId: string) => {
    setOpenManageId((prev) => (prev === projectId ? null : projectId));
  }, []);

  const handleManageSelect = useCallback(
    (projectId: string, key: 'posts' | 'donors' | 'payments' | 'projectSettings') => {
      setOpenManageId(null);
      const state = { projectId };
      if (key === 'posts') navigate(PATHS.POST_MANAGEMENT, { state });
      if (key === 'donors') navigate(PATHS.DONOR_MANAGEMENT, { state });
      if (key === 'payments') navigate(PATHS.PAYMENT_STATUS, { state });
      if (key === 'projectSettings') navigate(PATHS.PROJECT_SETTINGS, { state });
    },
    [navigate],
  );

  const handlePayRequest = useCallback((cardId: string, withdrawProjectId: string) => {
    setConfirmPayProject({ cardId, withdrawProjectId });
  }, []);

  const syncProjectStatus = useCallback(
    async (cardId: string, withdrawProjectId: string, nextStatus: 'VC' | 'PD') => {
    if (!userId || !cardId) return;

      try {
        const targetProject = (projects as any[]).find(
          (project) => String(project?.project_id ?? '') === cardId || String(project?._id ?? '') === withdrawProjectId,
        );
        const updatePayload = targetProject?.project_id
          ? { ...targetProject, project_id: String(targetProject.project_id), status: nextStatus }
          : { project_id: cardId, status: nextStatus };
        await dispatch(updateProject(updatePayload as any)).unwrap();
      } catch {
        // ignore status sync failure; keep pay flow moving
      }

      try {
        await dispatch(updateUser({ user_id: userId } as any)).unwrap();
      } catch {
        // ignore user sync failure; keep pay flow moving
      }
    },
    [dispatch, projects, userId],
  );

  const startPayFlow = useCallback(async (cardId: string, withdrawProjectId: string) => {
    if (!userId || !cardId) return;
    setPayFlowById((prev) => ({ ...prev, [cardId]: { step: 'processing' } }));
    window.clearTimeout(timersRef.current[cardId]);

    const openExistingWithdrawCodeInput = async (fallbackWithdrawId?: string) => {
      try {
        const projectSnapshot = (projects as any[]).find(
          (project) => String(project?.project_id ?? '') === cardId || String(project?._id ?? '') === withdrawProjectId,
        );
        const projectData: Record<string, unknown> = projectSnapshot && typeof projectSnapshot === 'object'
          ? (projectSnapshot as Record<string, unknown>)
          : {};
        const withdrawId = String(
          fallbackWithdrawId
          || (projectData as any)?.withdraw_id
          || '',
        ).trim();
        if (!withdrawId) {
          const fallbackFlow = resolveWithdrawPayFlow({
            snapshot: projectData,
            withdrawId: '',
            code: String((projectData as any)?.request_authentication_code ?? (projectData as any)?.authentication_code ?? ''),
            requestCode: String((projectData as any)?.request_authentication_code ?? (projectData as any)?.authentication_code ?? ''),
            fallbackFailCount: toTrialCount((projectData as any)?.user_trials ?? (projectData as any)?.trials, 0),
          });
          setPayFlowById((prev) => ({
            ...prev,
            [cardId]: fallbackFlow,
          }));
          return;
        }

        let withdrawSnapshot: Record<string, unknown> = { ...projectData };
            const detail = await dispatch(getWithdraw({ withdraw_id: withdrawId } as any)).unwrap();
            if (detail && typeof detail === 'object') {
          withdrawSnapshot = {
            ...projectData,
            ...(detail as Record<string, unknown>),
          };
            }

        const existingCode = String(
          (withdrawSnapshot as any)?.authentication_code
          ?? (withdrawSnapshot as any)?.request_authentication_code
          ?? (withdrawSnapshot as any)?.response_authentication_code
          ?? '',
        );
        const requestCode = String(
          (withdrawSnapshot as any)?.request_authentication_code
          ?? (withdrawSnapshot as any)?.authentication_code
          ?? '',
        );
        const fallbackFailCount = toTrialCount(
          (withdrawSnapshot as any)?.user_trials ?? (withdrawSnapshot as any)?.trials,
          0,
        );
        const resolvedPayFlow = resolveWithdrawPayFlow({
          snapshot: withdrawSnapshot,
          withdrawId,
            code: existingCode || requestCode,
            requestCode,
          fallbackFailCount,
        });
        setPayFlowById((prev) => ({
          ...prev,
          [cardId]: resolvedPayFlow,
        }));
        if (resolvedPayFlow.step === 'done') {
          await syncProjectStatus(cardId, withdrawProjectId, 'PD');
        }
      } catch {
        const projectSnapshot = (projects as any[]).find(
          (project) => String(project?.project_id ?? '') === cardId || String(project?._id ?? '') === withdrawProjectId,
        );
        const projectData: Record<string, unknown> = projectSnapshot && typeof projectSnapshot === 'object'
          ? (projectSnapshot as Record<string, unknown>)
          : {};
        const fallbackFlow = resolveWithdrawPayFlow({
          snapshot: projectData,
          withdrawId: String((projectData as any)?.withdraw_id ?? ''),
          code: String((projectData as any)?.request_authentication_code ?? (projectData as any)?.authentication_code ?? ''),
          requestCode: String((projectData as any)?.request_authentication_code ?? (projectData as any)?.authentication_code ?? ''),
          fallbackFailCount: toTrialCount((projectData as any)?.user_trials ?? (projectData as any)?.trials, 0),
        });
        setPayFlowById((prev) => ({
          ...prev,
          [cardId]: fallbackFlow,
        }));
      }
    };
    try {
      const response = await dispatch(
        createWithdraw({
          project_id: cardId,
          user_id: userId,
          device_id: userDeviceId,
          mobile_os_type: 'IOS',
        }),
      ).unwrap();
      const resultCode = Number((response as any)?.result_Code ?? (response as any)?.result_code ?? -1);
      const resultMessageText = String((response as any)?.result_message ?? '');
      const createdWithdrawId = String(
        (response as any)?.withdraw_id
        ?? (response as any)?.result_message?.withdraw_id
        ?? (response as any)?.result_message?.insertedId
        ?? '',
      ).trim();
      const isDuplicateRequest = resultCode === 60001 || /duplicate key/i.test(resultMessageText);
      if (resultCode === 0 || isDuplicateRequest) {
        await syncProjectStatus(cardId, withdrawProjectId, 'VC');
        await openExistingWithdrawCodeInput(createdWithdrawId);
        return;
      }

      setPayFlowById((prev) => ({ ...prev, [cardId]: { step: 'idle' } }));
    } catch (error) {
      const errorCode = Number((error as any)?.result_Code ?? (error as any)?.result_code ?? -1);
      const errorText = String((error as any)?.result_message ?? (error as any)?.message ?? '');
      const isDuplicateRequest = errorCode === 60001 || /duplicate key/i.test(errorText);
      if (isDuplicateRequest) {
        await syncProjectStatus(cardId, withdrawProjectId, 'VC');
        await openExistingWithdrawCodeInput();
        return;
      }
      console.error(error);
      setPayFlowById((prev) => ({ ...prev, [cardId]: { step: 'idle' } }));
    }
  }, [dispatch, projects, syncProjectStatus, userDeviceId, userId]);

  const updatePayInput = useCallback((projectId: string, next: string) => {
    const digitsOnly = next.replace(/\D/g, '');
    setPayFlowById((prev) => {
      const cur = prev[projectId];
      if (!cur || cur.step !== 'codeReady') return prev;
      return { ...prev, [projectId]: { ...cur, input: digitsOnly, error: undefined } };
    });
  }, []);

  const verifyPayCode = useCallback(
    async (projectId: string) => {
      const cur = payFlowById[projectId];
      if (!cur || cur.step !== 'codeReady') return;
      if (!cur.input.trim()) {
        setPayFlowById((prev) => ({
          ...prev,
          [projectId]: { ...cur, error: t('page.projectManagement.pay.invalidCode') },
        }));
        return;
      }

      try {
        const responseAuthenticationCodeAt = new Date().toISOString().replace(/\.\d{3}Z$/, '.000Z');
        const snapshot = (cur.withdrawSnapshot ?? {}) as Record<string, unknown>;
        const resolvedWithdrawId = String(
          cur.withdrawId
          || (snapshot as any)?.withdraw_id
          || '',
        ).trim();

        if (!resolvedWithdrawId) {
          setPayFlowById((prev) => ({
            ...prev,
            [projectId]: { ...cur, error: t('page.projectManagement.pay.invalidCode') },
          }));
          return;
        }

        // 서버 요구사항: /withdraw/get 응답값을 기준으로 put payload 구성
        let detailSnapshot: Record<string, unknown>;
        try {
          const detail = await dispatch(getWithdraw({ withdraw_id: resolvedWithdrawId } as any)).unwrap();
          detailSnapshot = (detail && typeof detail === 'object')
            ? (detail as Record<string, unknown>)
            : {};
        } catch {
          setPayFlowById((prev) => ({
            ...prev,
            [projectId]: { ...cur, error: t('page.projectManagement.pay.invalidCode') },
          }));
          return;
        }

        const putPayload: Record<string, unknown> = {
          ...detailSnapshot,
            request_authentication_code: cur.input.trim(),
            response_authentication_code_at: responseAuthenticationCodeAt,
        };
        if (!(putPayload as any).withdraw_id) {
          (putPayload as any).withdraw_id = resolvedWithdrawId;
        }

        const response = await dispatch(
          updateWithdraw(putPayload as any),
        ).unwrap();
        const resultCode = Number((response as any)?.result_Code ?? (response as any)?.result_code ?? -1);
        const statusCode = Number((response as any)?.statusCode ?? -1);
        if (resultCode === 0 || resultCode === 202 || statusCode === 200 || statusCode === 202) {
          let latestSnapshot: Record<string, unknown> = detailSnapshot;
          try {
            const latestDetail = await dispatch(getWithdraw({ withdraw_id: resolvedWithdrawId } as any)).unwrap();
            if (latestDetail && typeof latestDetail === 'object') {
              latestSnapshot = latestDetail as Record<string, unknown>;
            }
          } catch {
            // fallback to detailSnapshot from before /put
          }

          const latestCode = String(
            (latestSnapshot as any)?.authentication_code
            ?? (latestSnapshot as any)?.request_authentication_code
            ?? cur.input.trim(),
          );
          const latestRequestCode = String(
            (latestSnapshot as any)?.request_authentication_code
            ?? (latestSnapshot as any)?.authentication_code
            ?? cur.input.trim(),
          );
          const resolvedPayFlow = resolveWithdrawPayFlow({
            snapshot: latestSnapshot,
            withdrawId: resolvedWithdrawId,
            code: latestCode,
            requestCode: latestRequestCode,
            fallbackFailCount: cur.failCount,
          });

          setPayFlowById((prev) => ({
            ...prev,
            [projectId]: resolvedPayFlow,
          }));
          if (resolvedPayFlow.step === 'done') {
            await syncProjectStatus(projectId, projectId, 'PD');
          }
          setSuccessPayProjectId(projectId);
          return;
        }
      } catch (error) {
        console.error(error);
      }

      const nextFail = cur.failCount + 1;
      if (nextFail >= 3) {
        setPayFlowById((prev) => ({ ...prev, [projectId]: { step: 'stopped', failCount: nextFail } }));
        setFailedPayProjectId(projectId);
        return;
      }

      setPayFlowById((prev) => ({
        ...prev,
        [projectId]: { ...cur, failCount: nextFail, error: t('page.projectManagement.pay.invalidCode') },
      }));
    },
    [dispatch, payFlowById, syncProjectStatus, t],
  );

  const handleOpenProjectLink = useCallback((projectId: string) => {
    const url = `http://go.MissionFund.org/${projectId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleRequestConsulting = useCallback(() => {
    window.open('https://missionfund.org/support/', '_blank', 'noopener,noreferrer');
  }, []);

  const isDoneStatus = useCallback((statusValue?: string) => {
    const v = String(statusValue ?? '').toUpperCase();
    // 기본 종료 상태 판단(일시모금 VC/VS는 cards 계산에서 별도 처리)
    return v === 'DONE' || v === 'DN' || v === 'CM' || v === 'CL' || v === 'VC' || v === 'PD';
  }, []);

  useEffect(() => {
    setPayFlowById((prev) => {
      let changed = false;
      const next = { ...prev };

      projects.forEach((project) => {
        const cardId = String(project.project_id ?? project._id ?? project.title ?? '');
        if (!cardId) return;
        if (toFundingTypeLabel(project.type) !== 'oneTime') return;

        const existing = prev[cardId];
        if (existing && existing.step !== 'idle') return;

        const snapshot = (project && typeof project === 'object')
          ? (project as Record<string, unknown>)
          : {};
        const hasWithdrawSignals =
          'is_sent_authentication_code' in snapshot ||
          'is_response_authentication_code' in snapshot ||
          'is_sent_fund' in snapshot ||
          'user_trials' in snapshot ||
          'trials' in snapshot ||
          'withdraw_id' in snapshot;
        if (!hasWithdrawSignals) return;

        next[cardId] = resolveWithdrawPayFlow({
          snapshot,
          withdrawId: String((project as any)?.withdraw_id ?? ''),
          code: String((project as any)?.request_authentication_code ?? (project as any)?.authentication_code ?? ''),
          requestCode: String((project as any)?.request_authentication_code ?? (project as any)?.authentication_code ?? ''),
          fallbackFailCount: toTrialCount((project as any)?.user_trials ?? (project as any)?.trials, 0),
        });
        changed = true;
      });

      return changed ? next : prev;
    });
  }, [projects, toFundingTypeLabel]);

  const cards: ProjectCardItem[] = useMemo(() => {
    return projects.map((p) => {
      const fundingTypeLabel = toFundingTypeLabel(p.type);
      const projectStatus = String(p.status ?? '').toUpperCase();
      const isOneTimeRequestedStatus =
        fundingTypeLabel === 'oneTime' && (projectStatus === 'VC' || projectStatus === 'VS');
      const statusLabel: ProjectStatus = isOneTimeRequestedStatus
        ? 'inProgress'
        : isDoneStatus(projectStatus)
          ? 'done'
          : 'inProgress';
      const payAction: PayAction | undefined = fundingTypeLabel === 'oneTime' ? 'request' : undefined;
      return {
        id: String(p.project_id ?? p._id ?? p.title ?? ''),
        withdrawProjectId: String(p.project_id ?? p._id ?? ''),
        projectStatus,
        title: sanitizeProjectTitle(String(p.title ?? t('common.placeholders.untitledProject'))),
        dateText: String(p.start_date ?? p.created_at?.slice?.(0, 10) ?? ''),
        fundingTypeLabel,
        statusLabel,
        imageUrl: getCloudFrontImageUrl(getYouTubeThumbnail(p.video_url) || p.image_url, THUMBNAIL_DIMENSIONS),
        payAction,
      };
    });
  }, [isDoneStatus, projects, t, toFundingTypeLabel]);

  if (isLoading) {
    return (
      <WebPageLayout title={t('common.layout.projectManagement')}>
        <DSLoading marginTop="-40px" />
      </WebPageLayout>
    );
  }

  if (cards.length > 0) {
    return (
      <WebPageLayout title={t('common.layout.projectManagement')}>
        <Wrap>
          <TopRow>
            <SectionTitle>{t('page.projectManagement.sectionTitle')}</SectionTitle>
            <CreatePrimaryButton type="button" onClick={handleCreateProject}>
              <CreatePrimaryIcon
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 14 14"
                aria-hidden
                focusable="false"
              >
                <path d="M14 8H8V14H6V8H0V6H6V0H8V6H14V8Z" fill="white" />
              </CreatePrimaryIcon>
              <span>{t('page.projectManagement.createPrimary')}</span>
            </CreatePrimaryButton>
          </TopRow>

          <Cards>
            {cards.map((card) => {
              const payFlow = payFlowById[card.id] ?? ({ step: 'idle' } as PayFlowState);
              const isActiveCollecting = card.projectStatus === 'AC';
              const isProjectPaidDone = card.projectStatus === 'PD';
              const isProjectRequested = card.projectStatus === 'VC' || card.projectStatus === 'VS';
              const resolvedPayStep: PayFlowState['step'] =
                payFlow.step !== 'idle'
                  ? payFlow.step
                  : isActiveCollecting
                    ? 'idle'
                  : isProjectPaidDone
                    ? 'done'
                    : isProjectRequested
                      ? 'processing'
                      : 'idle';
              const codeReadyFlow = payFlow.step === 'codeReady' ? payFlow : null;
              const stoppedFailCount = payFlow.step === 'stopped' ? payFlow.failCount : 3;
              const isPayDone = resolvedPayStep === 'done';
              const isSettlementProcessing =
                resolvedPayStep === 'processing' || resolvedPayStep === 'inProgress';
              const displayStatusLabel: ProjectStatus = isPayDone ? 'done' : card.statusLabel;
              const isDone = displayStatusLabel === 'done';
              const isPayFailed = resolvedPayStep === 'stopped';
              const isCodeInputDisabled = resolvedPayStep === 'stopped';
              const isVerifyInputExpanded = Boolean(codeReadyFlow?.input) || focusedVerifyInputId === card.id;
              const isPayDisabled =
                isPayDone ||
                isPayFailed ||
                resolvedPayStep === 'processing' ||
                resolvedPayStep === 'inProgress' ||
                resolvedPayStep === 'codeReady';

              return (
                <Card key={card.id} $dimmed={isDone}>
                  <CardTopRow>
                  <CardLeft>
                    <Thumb
                      $hasImage={!!card.imageUrl}
                      style={
                        card.imageUrl
                          ? {
                              background: `linear-gradient(0deg, rgba(255, 255, 255, 0.32) 0%, rgba(255, 255, 255, 0.32) 100%), url(${card.imageUrl}) lightgray 50% / cover no-repeat`,
                            }
                          : undefined
                      }
                    >
                      {!card.imageUrl && (
                        <ThumbPlaceholder>
                          <ThumbLogo src={logoImg} alt="Mission Fund" />
                        </ThumbPlaceholder>
                      )}
                      <ChipRow>
                        <StatusChip $variant={displayStatusLabel}>
                          {displayStatusLabel === 'done' ? t('page.projectManagement.status.closed') : t(`page.projectManagement.status.${displayStatusLabel}`)}
                        </StatusChip>
                        <TypeChip
                          $noBorder={card.fundingTypeLabel === 'recurring'}
                          $dimmed={isDone}
                          $isOneTime={card.fundingTypeLabel === 'oneTime'}
                        >
                          {t(`page.projectManagement.fundingType.${card.fundingTypeLabel}`)}
                        </TypeChip>
                      </ChipRow>
                    </Thumb>
                  </CardLeft>

                  <CardRight>
                    <CardTitle title={card.title} onClick={() => handleOpenProjectLink(card.id)} role="button" tabIndex={0}>
                      {card.title}
                    </CardTitle>
                    <CardDate>{card.dateText}</CardDate>

                    <CardActions>
                      <ManageWrap ref={card.id === openManageId ? openManageWrapRef : undefined}>
                        <ManageButton
                          type="button"
                          onClick={() => handleManageProject(card.id)}
                          $open={card.id === openManageId}
                        >
                          {t('page.projectManagement.manage.button')} <ManageArrowIcon src={weuiArrowIcon} alt="" $open={card.id === openManageId} />
                        </ManageButton>
                        {card.id === openManageId ? (
                          <ManageMenu role="menu" aria-label="manage selector">
                            <ManageMenuItem type="button" onClick={() => handleManageSelect(card.id, 'posts')}>
                              <ManageMenuIcon src={postsIcon} alt="" /> {t('page.projectManagement.manage.posts')}
                            </ManageMenuItem>
                            <ManageMenuItem type="button" onClick={() => handleManageSelect(card.id, 'donors')}>
                              <ManageMenuIcon src={donorsIcon} alt="" /> {t('page.projectManagement.manage.donors')}
                            </ManageMenuItem>
                            <ManageMenuItem type="button" onClick={() => handleManageSelect(card.id, 'payments')}>
                              <ManageMenuIcon src={paymentsIcon} alt="" /> {t('page.projectManagement.manage.payments')}
                            </ManageMenuItem>
                            <ManageMenuItem
                              type="button"
                              onClick={() => handleManageSelect(card.id, 'projectSettings')}
                            >
                              <ManageMenuIcon src={settingsIcon} alt="" /> {t('page.projectManagement.manage.settings')}
                            </ManageMenuItem>
                          </ManageMenu>
                        ) : null}
                      </ManageWrap>

                      {card.payAction ? (
                        <PayButtonWrap>
                          <PayButton
                            type="button"
                            onClick={() => handlePayRequest(card.id, card.withdrawProjectId)}
                            disabled={isPayDisabled}
                            $done={isPayDone}
                            $failed={isPayFailed}
                            $processing={isSettlementProcessing}
                          >
                            {isPayDone
                              ? t('page.projectManagement.pay.status.completed')
                              : isPayFailed
                                ? t('page.projectManagement.pay.status.codeReady')
                                : isSettlementProcessing
                                  ? (resolvedPayStep === 'inProgress' ? t('page.projectManagement.pay.settlementInProgress') : t('page.projectManagement.pay.status.processing'))
                                  : resolvedPayStep === 'codeReady'
                                    ? t('page.projectManagement.pay.status.codeReady')
                                    : t('page.projectManagement.pay.status.request')}
                          </PayButton>
                          {!isPayDisabled ? <PayTooltip>{t('page.projectManagement.pay.hoverInfo')}</PayTooltip> : null}
                        </PayButtonWrap>
                      ) : null}

                      <LinkButton type="button" onClick={() => handleOpenProjectLink(card.id)}>
                        <LinkIconImg src={linkIcon} alt={t('page.projectManagement.linkAlt')} />
                      </LinkButton>
                    </CardActions>

                    {card.payAction && resolvedPayStep === 'inProgress' ? (
                      <PayInfoText>{t('page.projectManagement.pay.payoutInfo')}</PayInfoText>
                    ) : null}
                  </CardRight>
                  </CardTopRow>

                  {card.payAction && (resolvedPayStep === 'codeReady' || resolvedPayStep === 'stopped') ? (
                    <CardVerifyArea>
                      <VerifyWrap>
                        <VerifyRow>
                          <VerifyInputWrap
                            $error={Boolean(codeReadyFlow?.error) || resolvedPayStep === 'stopped'}
                            $disabled={isCodeInputDisabled}
                            $expanded={isVerifyInputExpanded}
                            $focused={focusedVerifyInputId === card.id}
                          >
                            {isVerifyInputExpanded ? (
                              <VerifyLabel>{t('page.projectManagement.pay.verifyLabel')}</VerifyLabel>
                            ) : null}
                            <VerifyInput
                              $expanded={isVerifyInputExpanded}
                              value={codeReadyFlow?.input ?? ''}
                              onChange={(e) => {
                                if (isCodeInputDisabled) return;
                                updatePayInput(card.id, e.target.value);
                              }}
                              onFocus={() => setFocusedVerifyInputId(card.id)}
                              onBlur={() => {
                                setFocusedVerifyInputId((prev) => (prev === card.id ? null : prev));
                              }}
                              placeholder={t('page.projectManagement.pay.verifyPlaceholder')}
                              disabled={isCodeInputDisabled}
                              inputMode="numeric"
                              pattern="[0-9]*"
                              type="tel"
                            />
                          </VerifyInputWrap>
                          <VerifyButton
                            type="button"
                            onClick={() => verifyPayCode(card.id)}
                            disabled={isCodeInputDisabled}
                          >
                            {t('page.projectManagement.pay.verifyButton')}
                          </VerifyButton>
                        </VerifyRow>
                        {resolvedPayStep === 'stopped'
                          ? <VerifyError>{t('page.projectManagement.pay.failStopped', { count: stoppedFailCount })}</VerifyError>
                          : codeReadyFlow?.error
                            ? <VerifyError>{codeReadyFlow.error}</VerifyError>
                            : null}
                        {resolvedPayStep === 'codeReady' && codeReadyFlow ? (
                          <VerifyHint>
                            {t('page.projectManagement.pay.failHint', { count: codeReadyFlow.failCount })}
                          </VerifyHint>
                        ) : null}
                      </VerifyWrap>
                    </CardVerifyArea>
                  ) : null}
                </Card>
              );
            })}
          </Cards>
        </Wrap>

        <DSModal
          open={Boolean(confirmPayProject)}
          title={t('page.projectManagement.pay.confirmTitle')}
          description={payConfirmDescription}
          descriptionVariant="body16"
          onClose={() => setConfirmPayProject(null)}
          actions={[
            { label: t('page.projectManagement.pay.confirmNo'), variant: 'secondary', onClick: () => setConfirmPayProject(null) },
            {
              label: t('page.projectManagement.pay.confirmYes'),
              variant: 'primary',
              onClick: () => {
                if (!confirmPayProject) return;
                const { cardId, withdrawProjectId } = confirmPayProject;
                setConfirmPayProject(null);
                startPayFlow(cardId, withdrawProjectId);
              },
            },
          ]}
        />

        <DSModal
          open={Boolean(successPayProjectId)}
          title={t('page.projectManagement.pay.successTitle')}
          description={paySuccessDescription}
          descriptionVariant="body16"
          onClose={() => setSuccessPayProjectId(null)}
          actions={[{ label: t('common.actions.confirm'), variant: 'primary', onClick: () => setSuccessPayProjectId(null) }]}
        />

        <DSModal
          open={Boolean(failedPayProjectId)}
          title={payFailTitle}
          description={payFailDescription}
          descriptionVariant="body16"
          onClose={() => setFailedPayProjectId(null)}
          actions={[{ label: t('common.actions.confirm'), variant: 'primary', onClick: () => setFailedPayProjectId(null) }]}
        />
        <ProfileIncompleteModal
          isOpen={isProfileIncompleteModalOpen}
          onClose={() => setIsProfileIncompleteModalOpen(false)}
          dismissible={false}
        />
      </WebPageLayout>
    );
  }

  return (
    <WebPageLayout title={t('common.layout.projectManagement')}>
      <Wrap>
        <SectionTitle>{t('page.projectManagement.sectionTitle')}</SectionTitle>

        <CreateCard type="button" onClick={handleCreateProject}>
          <CreateIcon
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 110 110"
            aria-hidden
            focusable="false"
          >
            <rect width="110" height="110" rx="55" fill="#2BC2E1" />
            <path d="M28 56.875V52.75H52.75V28H56.875V52.75H81.625V56.875H56.875V81.625H52.75V56.875H28Z" fill="white" />
          </CreateIcon>
          <CreateText>{t('page.projectManagement.createPrimary')}</CreateText>
        </CreateCard>
        <ProfileIncompleteModal
          isOpen={isProfileIncompleteModalOpen}
          onClose={() => setIsProfileIncompleteModalOpen(false)}
          dismissible={false}
        />

        <ConsultingBox>
          <ConsultingIcon src={hugeIcon} alt="" aria-hidden />
          <ConsultingRight>
            <ConsultingMessage>{t('page.projectManagement.consulting.message')}</ConsultingMessage>
            <ConsultingButton type="button" onClick={handleRequestConsulting}>
              <ButtonIcon
                xmlns="http://www.w3.org/2000/svg"
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                aria-hidden
                focusable="false"
              >
                <path d="M26.25 15L17.5 6.25V11.25C8.75 12.5 5 18.75 3.75 25C6.875 20.625 11.25 18.625 17.5 18.625V23.75L26.25 15Z" fill="white" />
              </ButtonIcon>
              {t('page.projectManagement.consulting.action')}
            </ConsultingButton>
          </ConsultingRight>
        </ConsultingBox>
      </Wrap>
    </WebPageLayout>
  );
};

const Wrap = styled.div`
  width: 100%;
  max-width: ${convertToVwWeb(898)};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(20)};
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;;
  justify-content: space-between;
  gap: ${convertToVwWeb(16)};
  margin-top: ${convertToVwWeb(20)};
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.grayScale[90]};
  ${({ theme }) => theme.typography.head.semiBold24};
`;

const CreatePrimaryButton = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${cappedVwWeb(8)};
  padding: 14px 44px;
  border-radius: 50px;
  border: none;
  background: var(--PrimaryBlue-70, #357B89);
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);

  color: ${({ theme }) => theme.colors.grayScale[0]};
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%;
  cursor: pointer;

  &:hover {
    border-radius: 50px;
    background: #026478;
    box-shadow: 0 0 5.8px 0 #026478;
  }

  &:focus,
  &:active {
    border-radius: 50px;
    background: #416777;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  }
`;

const CreatePrimaryIcon = styled.svg`
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
`;

const CreateCard = styled.button`
  width: 100%;
  height: ${convertToVwWeb(245)};
  padding: ${convertToVwWeb(43)} ${convertToVwWeb(24)};
  border-radius: ${convertToVwWeb(20)};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  background: ${({ theme }) => theme.colors.other.mainBackground};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwWeb(20)};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    border-radius: ${convertToVwWeb(20)};
    background: #F8FDFF;
  }

  &:focus,
  &:focus-visible,
  &:active {
    border-radius: ${convertToVwWeb(20)};
    background: #EBFCFF;
    outline: none;
  }
`;

const CreateIcon = styled.svg`
  width: ${convertToVwWeb(110)};
  height: ${convertToVwWeb(110)};
  flex: 0 0 auto;
  transition: filter 0.2s ease;

  ${CreateCard}:hover & {
    filter: drop-shadow(0 0 ${convertToVwWeb(9.4)} #2BC2E1);
  }

  ${CreateCard}:focus &,
  ${CreateCard}:focus-visible &,
  ${CreateCard}:active & {
    filter: drop-shadow(0 0 ${convertToVwWeb(9.4)} #2BC2E1)
      drop-shadow(0 0 ${convertToVwWeb(20.45)} #2BC2E1);
  }
`;

const CreateText = styled.div`
  color: #2bc2e1;
  text-align: center;
  font-size: ${convertToVwWeb(24)};
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
`;

const ConsultingBox = styled.section`
  width: 100%;
  padding: ${convertToVwWeb(18)} ${convertToVwWeb(22)};
  border-radius: ${convertToVwWeb(20)};
  border: 1px solid #fff3d0;
  background: #fffaea;
  display: flex;
  align-items: flex-start;
  gap: ${convertToVwWeb(16)};
  margin-bottom: ${convertToVwWeb(20)};
`;

const ConsultingRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${convertToVwWeb(10)};
  min-width: 0;
`;

const ConsultingIcon = styled.img`
  width: ${convertToVwWeb(28)};
  height: ${convertToVwWeb(28)};
  flex: 0 0 auto;
`;

const ConsultingMessage = styled.p`
  color: #17171b;
  font-size: ${convertToVwWeb(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  min-width: 0;
`;

const ConsultingButton = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: ${convertToVwWeb(8)};
  padding: ${convertToVwWeb(12)} ${convertToVwWeb(20)};
  border-radius: 999px;
  border: none;
  background: #ffd55b;
  color: #ffffff;
  font-size: ${convertToVwWeb(20)};
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  cursor: pointer;

  &:hover {
    border-radius: 50px;
    background: #FFBE00;
    box-shadow: 0 0 5.4px 0 #FFBE00;
  }

  &:focus,
  &:focus-visible {
    border-radius: 50px;
    background: #FFAE00;
  }
`;

const ButtonIcon = styled.svg`
  width: ${convertToVwWeb(30)};
  height: ${convertToVwWeb(30)};
  flex: 0 0 auto;
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwWeb(16)};
  margin-bottom: ${convertToVwWeb(140)};
`;

const Card = styled.section<{ $dimmed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${convertToVwWeb(22)};
  padding: ${convertToVwWeb(18)};
  border-radius: ${convertToVwWeb(12)};
  border: 1px solid #eee;
  background: ${({ $dimmed }) => ($dimmed ? '#F0F1F2' : '#ffffff')};
`;

/** 썸네일 + 제목·버튼 줄: 줄바꿈 금지 → 긴 제목은 오른쪽에서 2줄 말줄임 */
const CardTopRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: ${convertToVwWeb(22)};
  width: 100%;
  min-width: 0;
`;

const CardLeft = styled.div`
  flex: 0 0 auto;
  flex-shrink: 0;
`;

const Thumb = styled.div<{ $hasImage?: boolean }>`
  position: relative;
  width: ${convertToVwWeb(274)};
  height: ${convertToVwWeb(208)};
  max-height: ${convertToVwWeb(208)};
  border-radius: 12px;
  border: 1px solid #eee;
  overflow: hidden;
  background: ${({ $hasImage }) => ($hasImage ? 'transparent' : '#f3f3f3')};
`;

const ThumbPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f3f3;
`;

const ThumbLogo = styled.img`
  width: ${convertToVwWeb(110)};
  height: auto;
  opacity: 0.85;
`;

const ChipRow = styled.div`
  position: absolute;
  top: ${convertToVwWeb(12)};
  left: ${convertToVwWeb(12)};
  display: flex;
  align-items: center;
  gap: ${convertToVwWeb(8)};
`;

const BaseChip = styled.div`
  display: flex;
  padding: ${convertToVwWeb(4)} ${convertToVwWeb(12)};
  justify-content: center;
  align-items: center;
  gap: ${convertToVwWeb(10)};
  border-radius: 4px;
  font-family: Pretendard;
  font-size: ${convertToVwWeb(16)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  white-space: nowrap;
`;

const StatusChip = styled(BaseChip)<{ $variant: 'inProgress' | 'done' }>`
  background: ${({ $variant }) => ($variant === 'inProgress' ? '#2bc2e1' : '#929292')};
  color: ${({ $variant }) => ($variant === 'inProgress' ? '#ffffff' : '#E5E5E5')};
  border-radius: 4px;
`;

const TypeChip = styled(BaseChip)<{ $noBorder?: boolean; $dimmed?: boolean; $isOneTime?: boolean }>`
  background: #ffffff;
  border: ${({ $noBorder, $dimmed, $isOneTime }) => ($dimmed || $noBorder || $isOneTime ? 'none' : '1px solid #2bc2e1')};
  color: ${({ $dimmed, $isOneTime }) => ($dimmed || $isOneTime ? 'var(--grey-scale-90-main-text, #17171B)' : '#2bc2e1')};
`;

const CardRight = styled.div`
  min-width: 0;
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${convertToVwWeb(2)};
  height: ${convertToVwWeb(208)};
  max-height: ${convertToVwWeb(208)};
  overflow: visible;
`;

const CardVerifyArea = styled.div`
  width: calc(100% + (${convertToVwWeb(18)} * 2));
  margin-left: calc(${convertToVwWeb(17.1)} * -1);
  margin-right: calc(${convertToVwWeb(17.1)} * -1);
  margin-bottom: calc(${convertToVwWeb(17)} * -1);
  border-radius: 0 0 ${convertToVwWeb(12)} ${convertToVwWeb(12)};
`;

const CardTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwWeb(24)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  overflow-wrap: anywhere;
  cursor: pointer;
`;

const CardDate = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwWeb(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  margin-bottom: ${convertToVwWeb(40)};
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${convertToVwWeb(8)};
  margin-top: auto;
`;

const ManageWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: ${convertToVwWeb(200)};
`;

const ManageArrowIcon = styled.img<{ $open?: boolean }>`
  width: ${convertToVwWeb(24)};
  height: ${convertToVwWeb(24)};
  object-fit: contain;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
  transition: transform 0.2s ease, filter 0.2s ease;
  filter: ${({ $open }) =>
    $open
      ? 'invert(79%) sepia(32%) saturate(4383%) hue-rotate(158deg) brightness(100%) contrast(91%)'
      : 'none'};
`;

const ManageButton = styled.button<{ $open?: boolean }>`
  display: inline-flex;
  width: 100%;
  height: ${convertToVwWeb(54)};
  justify-content: center;
  align-items: center;
  gap: ${convertToVwWeb(10)};
  border-radius: ${convertToVwWeb(12)};
  border: ${convertToVwWeb(1.6)} solid ${({ $open }) => ($open ? '#2bc2e1' : '#e5e5e5')};
  background: #ffffff;
  color: ${({ $open }) => ($open ? '#2bc2e1' : '#17171b')};
  font-family: Pretendard;
  font-size: ${convertToVwWeb(20)};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 ${convertToVwWeb(2)} ${convertToVwWeb(8)} rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    border-color: #2bc2e1;
    background: ${({ $open }) => ($open ? '#ffffff' : '#f8fdff')};
    color: ${({ theme, $open }) => ($open ? theme.colors.primaryBlue[40] : '#2bc2e1')};
    box-shadow: 0 ${convertToVwWeb(4)} ${convertToVwWeb(12)} rgba(0, 0, 0, 0.08);

    img {
      /* $open 시 primaryBlue[70], 아니면 primaryBlue[40] */
      filter: ${({ $open }) =>
        $open
          ? 'brightness(0) saturate(100%) invert(42%) sepia(26%) saturate(1843%) hue-rotate(158deg) brightness(92%) contrast(89%)'
          : 'invert(79%) sepia(32%) saturate(4383%) hue-rotate(158deg) brightness(100%) contrast(91%)'};
    }
  }

  &:active:not(:disabled) {
    border-color: #2bc2e1;
    background: ${({ $open }) => ($open ? '#ffffff' : '#ebfcff')};
    color: ${({ theme, $open }) => ($open ? theme.colors.primaryBlue[40] : '#2bc2e1')};

    img {
      filter: ${({ $open }) =>
        $open
          ? 'brightness(0) saturate(100%) invert(42%) sepia(26%) saturate(1843%) hue-rotate(158deg) brightness(92%) contrast(89%)'
          : 'invert(79%) sepia(32%) saturate(4383%) hue-rotate(158deg) brightness(100%) contrast(91%)'};
    }
  }

  &:focus:not(:disabled) {
    outline: none;
    border-color: #2bc2e1;
    background: ${({ $open }) => ($open ? '#ffffff' : '#ebfcff')};
    color: ${({ theme, $open }) => ($open ? theme.colors.primaryBlue[40] : '#2bc2e1')};

    img {
      filter: ${({ $open }) =>
        $open
          ? 'brightness(0) saturate(100%) invert(42%) sepia(26%) saturate(1843%) hue-rotate(158deg) brightness(92%) contrast(89%)'
          : 'invert(79%) sepia(32%) saturate(4383%) hue-rotate(158deg) brightness(100%) contrast(91%)'};
    }
  }

  .anticon {
    font-size: ${convertToVwWeb(14)};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    border-color: #e5e5e5;
    color: #17171b;
    background: #ffffff;
    box-shadow: none;
  }
`;

const ManageMenu = styled.div`
  position: absolute;
  top: calc(100% + ${convertToVwWeb(8)});
  left: 0;
  width: 100%;
  border-radius: ${convertToVwWeb(14)};
  background: #ffffff;
  box-shadow: 0 ${convertToVwWeb(12)} ${convertToVwWeb(32)} rgba(0, 0, 0, 0.15);
  padding: ${convertToVwWeb(10)} 0;
  z-index: 20;
  border: 1px solid #f0f1f2;
`;

const ManageMenuIcon = styled.img`
  width: ${convertToVwWeb(20)};
  height: ${convertToVwWeb(20)};
  object-fit: contain;
  flex-shrink: 0;
  opacity: 0.75;
`;

const ManageMenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${convertToVwWeb(12)};
  padding: ${convertToVwWeb(14)} ${convertToVwWeb(20)};
  border: 0;
  background: ${({ theme }) => theme.colors.grayScale[0]};
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwWeb(18)};
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryBlue[5]};
    color: ${({ theme }) => theme.colors.primaryBlue[40]};

    img {
      opacity: 1;
      /* primaryBlue[70] #357B89 */
      filter: brightness(0) saturate(100%) invert(42%) sepia(26%) saturate(1843%) hue-rotate(158deg) brightness(92%) contrast(89%);
    }
  }

  &:focus {
    outline: none;
    background: ${({ theme }) => theme.colors.primaryBlue[10]};
    color: ${({ theme }) => theme.colors.primaryBlue[40]};

    img {
      opacity: 1;
      filter: brightness(0) saturate(100%) invert(42%) sepia(26%) saturate(1843%) hue-rotate(158deg) brightness(92%) contrast(89%);
    }
  }
`;

const PayInfoText = styled.div`
  margin-top: ${convertToVwWeb(10)};
  color: ${({ theme }) => theme.colors.primaryBlue[70]};
  font-family: Pretendard;
  font-size: ${convertToVwWeb(16)};
  font-weight: 400;
  line-height: 150%;
  white-space: pre-line;
`;

const VerifyWrap = styled.div`
  width: 100%;
  box-sizing: border-box;
  border-radius: 0 0 ${convertToVwWeb(12)} ${convertToVwWeb(12)};
  background: #f8fdff;
  padding: ${convertToVwWeb(12)} ${convertToVwWeb(12)} ${convertToVwWeb(12)}
    calc(${convertToVwWeb(12)} + ${convertToVwWeb(274)} + ${convertToVwWeb(22)});
`;

const VerifyRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${convertToVwWeb(12)};
  width: calc(${convertToVwWeb(200)} + ${convertToVwWeb(200)} + ${convertToVwWeb(50)} + ${convertToVwWeb(16)});
  max-width: 100%;
`;

const VerifyInputWrap = styled.div<{ $error: boolean; $disabled?: boolean; $expanded: boolean; $focused?: boolean }>`
  flex: 1 1 auto;
  height: ${({ $expanded }) => ($expanded ? convertToVwWeb(74) : convertToVwWeb(44))};
  border-radius: ${convertToVwWeb(12)};
  border: 1px solid ${({ $error, $disabled, $focused }) =>
    ($disabled ? '#e3e5e8' : $error ? '#ff0f0f' : $focused ? '#2bc2e1' : '#e3e5e8')};
  background: ${({ $disabled }) => ($disabled ? '#f5f6f7' : '#ffffff')};
  padding: ${({ $expanded }) => ($expanded ? `${convertToVwWeb(10)} ${convertToVwWeb(14)}` : `0 ${convertToVwWeb(14)}`)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
  transition: height 0.2s ease, padding 0.2s ease, border-color 0.2s ease;
`;

const VerifyLabel = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwWeb(14)};
  font-weight: 500;
  line-height: 120%;
`;

const VerifyInput = styled.input<{ $expanded: boolean }>`
  margin-top: ${({ $expanded }) => ($expanded ? convertToVwWeb(6) : '0')};
  width: 100%;
  height: ${({ $expanded }) => ($expanded ? 'auto' : '100%')};
  border: 0;
  outline: none;
  font-family: Pretendard;
  font-size: ${convertToVwWeb(18)};
  font-weight: ${({ $expanded }) => ($expanded ? 700 : 500)};
  line-height: ${({ $expanded }) => ($expanded ? '150%' : convertToVwWeb(44))};
  color: #17171b;
  background: transparent;
  padding: 0;

  &::placeholder {
    color: #b5b7bb;
    font-weight: 500;
  }

  &:disabled {
    background: transparent;
    color: #b5b7bb;
    cursor: not-allowed;
  }
`;

const VerifyButton = styled.button`
  flex: 0 0 auto;
  height: ${convertToVwWeb(44)};
  padding: 0 ${convertToVwWeb(20)};
  border-radius: ${convertToVwWeb(999)};
  border: 0;
  background: #357b89;
  color: #ffffff;
  font-family: Pretendard;
  font-size: ${convertToVwWeb(18)};
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    background: #f0f1f3;
    color: #b5b7bb;
    cursor: not-allowed;
  }
`;

const VerifyError = styled.div`
  margin-top: ${convertToVwWeb(8)};
  color: #ff0f0f;
  font-family: Pretendard;
  font-size: ${convertToVwWeb(14)};
  font-weight: 500;
  line-height: 150%;
`;

const VerifyHint = styled.div`
  margin-top: ${convertToVwWeb(6)};
  color: #ff0f0f;
  font-family: Pretendard;
  font-size: ${convertToVwWeb(14)};
  font-weight: 500;
  line-height: 150%;
`;

const PayTooltip = styled.div`
  position: absolute;
  left: 50%;
  bottom: calc(100% + ${convertToVwWeb(8)});
  transform: translate(-50%, ${convertToVwWeb(6)});
  padding: ${convertToVwWeb(10)} ${convertToVwWeb(16)};
  border-radius: ${convertToVwWeb(12)};
  background: #2bc2e1;
  color: #ffffff;
  font-family: Pretendard;
  font-size: ${convertToVwWeb(16)};
  font-weight: 500;
  line-height: 150%;
  text-align: center;
  white-space: pre-line;
  box-shadow: 0 ${convertToVwWeb(8)} ${convertToVwWeb(16)} rgba(0, 0, 0, 0.12);
  width: 100%;
  box-sizing: border-box;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
  z-index: 5;
`;

const PayButtonWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  &:hover ${PayTooltip},
  &:focus-within ${PayTooltip} {
    opacity: 1;
    visibility: visible;
    transform: translate(-50%, 0);
  }
`;

const PayButton = styled.button<{ $done: boolean; $failed?: boolean; $processing?: boolean }>`
  display: inline-flex;
  width: ${convertToVwWeb(200)};
  height: ${convertToVwWeb(54)};
  padding: 0 ${convertToVwWeb(24)};
  justify-content: center;
  align-items: center;
  border-radius: ${convertToVwWeb(12)};
  border: ${convertToVwWeb(1.6)} solid #e5e5e5;
  background: ${({ $done }) => ($done ? '#bdbdbd' : '#ffffff')};
  color: ${({ $done, $failed, $processing }) =>
    $done ? '#ffffff' : $failed ? 'var(--grey-scale-50-disabled-text, #BCBCBC)' : $processing ? 'var(--Primary-Skyblue500, #357B89)' : '#17171b'};
  font-family: Pretendard;
  font-size: ${({ $processing }) => ($processing ? convertToVwWeb(20) : convertToVwWeb(20))};
  font-weight: 600;
  line-height: 150%;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #2bc2e1;
    background: #f8fdff;
    color: #2bc2e1;
  }

  &:focus-visible:not(:disabled) {
    outline: none;
    border-color: #2bc2e1;
    background: #ebfcff;
    color: #2bc2e1;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: ${({ $failed, $processing }) => ($failed || $processing ? 1 : 0.7)};
  }
`;

const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${convertToVwWeb(50)};
  height: ${convertToVwWeb(50)};
  border-radius: ${convertToVwWeb(12)};
  border: none;
  background: ${({ theme }) => theme.colors.primaryBlue[15]};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryBlue[20]};
  }

  &:focus:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryBlue[30]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const LinkIconImg = styled.img`
  width: ${convertToVwWeb(22)};
  height: ${convertToVwWeb(22)};
  object-fit: contain;
`;

export default ProjectManagementWeb;


